import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Rocket } from "lucide-react";
import { useState } from "react";

interface BoostListingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBoost: (boostOption: "30min" | "6h" | "24h") => void;
  propertyTitle?: string;
  isBoosting?: boolean;
}

const boostOptions: {
  value: "30min" | "6h" | "24h";
  label: string;
  price: string;
  description: string;
}[] = [
  {
    value: "30min",
    label: "30 Minutes",
    price: "10 Points",
    description: "Quick boost for immediate visibility",
  },
  {
    value: "6h",
    label: "6 Hours",
    price: "50 Points",
    description: "Extended visibility for half a day",
  },
  {
    value: "24h",
    label: "24 Hours",
    price: "100 Points",
    description: "Full day of increased exposure",
  },
];

export function BoostListingDialog({
  open,
  onOpenChange,
  onBoost,
  propertyTitle,
  isBoosting,
}: BoostListingDialogProps) {
  const [selectedOption, setSelectedOption] = useState<
    "30min" | "6h" | "24h" | null
  >(null);

  const handleBoostClick = () => {
    if (selectedOption !== null) {
      onBoost(selectedOption);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedOption(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-center flex flex-col items-center gap-2">
            <div className="bg-blue-100 p-3 rounded-full">
              <Rocket className="h-6 w-6 text-blue-600 stroke-2" />
            </div>
            Boost Your Listing
          </DialogTitle>
          <DialogDescription className="text-center">
            {propertyTitle
              ? `Boost "${propertyTitle}" to gain maximum exposure and reach more potential clients.`
              : "Boost your property to gain maximum exposure and reach more potential clients."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {boostOptions.map((option, index) => (
            <div
              key={option.value}
              className={`relative flex items-center justify-between p-4 rounded-lg border transition-all cursor-pointer ${
                selectedOption === option.value
                  ? "border-propertylk-blue bg-propertylk-tag"
                  : "border-gray-200 hover:border-propertylk-blue/50"
              }`}
              onClick={() => setSelectedOption(option.value)}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                    selectedOption === option.value
                      ? "border-propertylk-blue"
                      : "border-gray-300"
                  }`}
                >
                  {selectedOption === option.value && (
                    <div className="w-3 h-3 rounded-full bg-propertylk-blue" />
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{option.label}</h4>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </div>
              <span className="font-bold text-propertylk-darkblue">
                {option.price}
              </span>
            </div>
          ))}
        </div>

        <DialogFooter className="flex justify-end gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isBoosting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBoostClick}
            disabled={selectedOption === null || isBoosting}
          >
            {isBoosting ? "Boosting..." : "Boost Now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
